import { isAxiosError } from 'axios'
import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  combineMutationResponseStatusFlags,
  USE_COMBINE_MUTATION_RESULT_FLAGS
} from 'components/status-checks/hooks/use-validate-configuration-utils'
import { useValidateEndpointConfiguration } from 'components/status-checks/hooks/use-validate-endpoint-configuration'
import { useValidateEvaluationConfiguration } from 'components/status-checks/hooks/use-validate-evaluation-configuration'
import { type StatusCheckFormEvaluationConfiguration } from 'components/status-checks/status-checks-form-utils'
import toast from 'lib/toast'
import {
  type EndpointConfiguration,
  type TeamExternalIntegration
} from 'types/api/status-checks/status-check'
import { type MultiValueTags } from 'types/tags'
import { getMessageFromResponseOrError } from 'utils/strings'

interface UseValidateAllConfigurationsParams {
  endpointConfiguration?: EndpointConfiguration
  evaluationConfiguration: StatusCheckFormEvaluationConfiguration
  integration: TeamExternalIntegration
  statusCheckMultiSelectTags?: MultiValueTags
}

export function useValidateAllConfigurations({
  endpointConfiguration,
  evaluationConfiguration,
  integration,
  statusCheckMultiSelectTags
}: UseValidateAllConfigurationsParams) {
  const [abortController, setAbortController] = useState(new AbortController())

  const {
    reset: resetValidation,
    validateEndpointConfiguration,
    ...validateEndpointConfigurationResponse
  } = useValidateEndpointConfiguration(abortController)

  const {
    reset: resetEvaluationValidation,
    validateEvaluationConfiguration,
    ...validateEvaluationConfigurationResponse
  } = useValidateEvaluationConfiguration(abortController)

  // Derived validation state flags
  const { isCanceled, isError, isLoading, isSuccess } =
    combineMutationResponseStatusFlags(
      [...USE_COMBINE_MUTATION_RESULT_FLAGS, 'isCanceled'],
      [
        validateEndpointConfigurationResponse,
        validateEvaluationConfigurationResponse
      ]
    )

  const resetValidations = useCallback(() => {
    resetValidation()
    resetEvaluationValidation()
  }, [resetEvaluationValidation, resetValidation])

  const validateEndpointAndEvaluationConfigurations = useCallback(async () => {
    try {
      // First, validate the endpoint configuration. The hook handles PNI vs public integration cases internally.
      const { testEndpointResponse } = await validateEndpointConfiguration(
        integration,
        { endpointConfiguration, statusCheckMultiSelectTags }
      )

      // Second, validate the evaluation configuration against the endpoint response. Again PNI vs public cases are handled internally.
      const { testEvaluationResults } = await validateEvaluationConfiguration({
        endpointConfiguration,
        endpointResponse: testEndpointResponse,
        evaluationConfiguration,
        integration,
        statusCheckMultiSelectTags
      })

      return [testEndpointResponse, testEvaluationResults] as const
    } catch (error: unknown) {
      // In the event this is not an axios error, at least throw an error toast notification to inform the user.
      if (!isAxiosError(error)) {
        const errorText = getMessageFromResponseOrError(
          error,
          'An unknown error occurred while validating the Health Check.'
        )

        toast({ type: 'error', text: errorText })

        return Promise.reject()
      }

      return Promise.reject(error)
    }
  }, [
    endpointConfiguration,
    evaluationConfiguration,
    integration,
    statusCheckMultiSelectTags,
    validateEndpointConfiguration,
    validateEvaluationConfiguration
  ])

  // Re-creates the abort controller if the previous one was aborted.
  useEffect(() => {
    if (!abortController.signal.aborted) return

    setAbortController(new AbortController())
  }, [abortController.signal.aborted])

  return useMemo(
    () => ({
      abortController,
      hasError: isError,
      isCanceled,
      isValidating: isLoading,
      isValidated: isSuccess,
      resetValidations,
      validateEndpointAndEvaluationConfigurations
    }),
    [
      abortController,
      isError,
      isCanceled,
      isLoading,
      isSuccess,
      resetValidations,
      validateEndpointAndEvaluationConfigurations
    ]
  )
}
